import { useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { useDbValue } from '../lib/useDbValue.js'
import { pickPremiumCurrentEventId } from '../lib/pickCurrentEvent.js'
import { useSetAdminNav } from './AdminShell.jsx'
import { fourBallNavItems } from '../components/PbccLayout.jsx'

// Code-gated Calcutta entry from the admin home. The gate (code 2026) is just to
// get in from the home; once past it we resolve the current/active tournament the
// SAME way the AI Assistant / TV boards do (pickPremiumCurrentEventId) and REDIRECT
// into the REAL Calcutta tab (/admin/t/<id>?tab=Calcutta) — so the page is
// identical to the normal Calcutta page: same header, left nav, tabs, and the
// "← All tournaments" back button (no more dead-end). If no tournament exists yet,
// it shows the ready empty-state note WITH the admin nav so it's never stuck.
const CALCUTTA_CODE = '2026'

// "Keep Calcutta unlocked" flag, set by the in-Calcutta toggle (localStorage).
// When set, the gate is skipped; otherwise the 2026 code is required on EVERY entry.
function keepCalcuttaUnlocked() {
  try { return localStorage.getItem('calcutta_keep_unlocked') === '1' } catch { return false }
}

export default function CalcuttaStandalone() {
  // `unlocked` is NOT persisted, so the gate RE-LOCKS every time you leave:
  // re-entering /admin/calcutta re-mounts fresh and re-prompts for 2026 — unless
  // the admin turned ON "Keep Calcutta unlocked" inside Calcutta.
  const [unlocked, setUnlocked] = useState(false)
  if (!unlocked && !keepCalcuttaUnlocked()) {
    return <CalcuttaGate onUnlock={() => setUnlocked(true)} />
  }
  return <CalcuttaRedirect />
}

function CalcuttaGate({ onUnlock }) {
  const [code, setCode] = useState('')
  const [err, setErr] = useState('')
  const submit = (e) => {
    e?.preventDefault?.()
    if (code.trim() === CALCUTTA_CODE) onUnlock()
    else setErr('Wrong code — no entry.')
  }
  return (
    <div className="max-w-sm mx-auto mt-20 px-4">
      <div className="card text-center">
        <div className="text-4xl mb-1">🎙️</div>
        <h1 className="text-xl font-extrabold text-green-deep">Calcutta</h1>
        <p className="text-sm text-gray-500 mt-1 mb-4">Enter the access code to open the Calcutta control.</p>
        <form onSubmit={submit}>
          <input
            type="password" inputMode="numeric" autoFocus value={code}
            onChange={(e) => { setCode(e.target.value); setErr('') }}
            placeholder="Access code"
            className="w-full text-center text-lg font-bold tracking-widest rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:border-green-deep" />
          {err ? <p className="text-sm text-red-600 mt-2 font-semibold">{err}</p> : null}
          <button type="submit" className="mt-3 w-full rounded-lg px-4 py-2.5 font-extrabold text-white" style={{ background: 'var(--ge-primary)' }}>Enter Calcutta →</button>
        </form>
      </div>
    </div>
  )
}

function CalcuttaRedirect() {
  const { data: all, status } = useDbValue('tournaments')
  const tid = useMemo(() => pickPremiumCurrentEventId(all || {}), [all])
  if (status === 'loading') return <div className="max-w-3xl mx-auto px-6 py-10 text-gray-500">Loading Calcutta…</div>
  // A tournament exists → open the REAL Calcutta tab (full chrome + nav + back).
  if (tid) return <Navigate to={`/admin/t/${tid}?tab=Calcutta`} replace />
  return <NoTournamentReady />
}

// No tournament created yet — ready empty state. Publishes the standard admin nav
// (back to home + New Registration/Qualifying/Tournament) so the user is never
// stranded, and shows the "build the field" note.
function NoTournamentReady() {
  const navigate = useNavigate()
  useSetAdminNav({
    back: { to: '/admin/pbcc-4ball', label: '← Admin Home' },
    items: fourBallNavItems(navigate).map((it) => ({ key: it.key, label: it.label, onClick: it.action })),
  }, [])
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-extrabold text-green-deep mb-3">🎙️ Calcutta</h1>
      <div className="card" style={{ background: '#FFF7E6', border: '1px solid #F0D48A' }}>
        <p className="font-bold text-amber-900">No tournament yet</p>
        <p className="text-sm text-amber-800 mt-1">Build the field into a tournament to start the auction — Calcutta populates automatically once a tournament with flights exists.</p>
      </div>
    </div>
  )
}
